import { useState, useCallback, useEffect } from "react";
import { MonitoringConfig } from "../types/add-brand-types";

interface Platform {
  id: string;
  name: string;
  enabled: boolean;
}

export const useMonitoringSettings = (
  monitoringConfig: MonitoringConfig,
  onUpdateKeywordClick: (keywords: string[], platforms: string[]) => void,
  onClose: () => void
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [platforms, setPlatforms] = useState<Platform[]>([
    { id: "reddit", name: "Reddit", enabled: false },
    { id: "twitter", name: "Twitter", enabled: false },
    { id: "linkedIn", name: "LinkedIn", enabled: false },
    { id: "quora", name: "Quora", enabled: false },
  ]);

  const [keywords, setKeywords] = useState<string[]>(
    monitoringConfig.keywords || []
  );

  const [keywordInput, setKeywordInput] = useState("");

  useEffect(() => {
    const enabledPlatformIds = monitoringConfig.monitoring_platforms || [];
    setPlatforms([
      {
        id: "reddit",
        name: "Reddit",
        enabled: enabledPlatformIds.includes("reddit"),
      },
      {
        id: "twitter",
        name: "Twitter",
        enabled: enabledPlatformIds.includes("twitter"),
      },
      {
        id: "linkedIn",
        name: "LinkedIn",
        enabled: enabledPlatformIds.includes("linkedIn"),
      },
      {
        id: "quora",
        name: "Quora",
        enabled: enabledPlatformIds.includes("quora"),
      },
    ]);
  }, [monitoringConfig.monitoring_platforms]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose();
  }, [onClose]);

  const togglePlatform = useCallback((platformId: string) => {
    setPlatforms((prev) =>
      prev.map((platform) =>
        platform.id === platformId
          ? { ...platform, enabled: !platform.enabled }
          : platform
      )
    );
  }, []);

  const addKeyword = useCallback(() => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords((prev) => [...prev, keywordInput.trim()]);
      setKeywordInput("");
    }
  }, [keywordInput, keywords]);

  const removeKeyword = useCallback((keyword: string) => {
    setKeywords((prev) => prev.filter((k) => k !== keyword));
  }, []);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        addKeyword();
      }
    },
    [addKeyword]
  );

  const handleSave = useCallback(() => {
    onUpdateKeywordClick(
      keywords,
      platforms
        .filter((platform) => platform.enabled)
        .map((platform) => platform.id)
    );
    handleClose();
  }, [handleClose, keywords, onUpdateKeywordClick, platforms]);

  return {
    isOpen,
    platforms,
    keywords,
    keywordInput,
    setKeywordInput,
    handleOpen,
    handleClose,
    togglePlatform,
    addKeyword,
    removeKeyword,
    handleKeyPress,
    handleSave,
  };
};
